const DESIGN_SYSTEM_THEMES = ["light", "dark"] as const;
const DESIGN_SYSTEM_PREVIEWS = ["asset-selector"] as const;
/** Legacy single-page section values, still accepted for redirect mapping. */
const DESIGN_SYSTEM_LEGACY_SECTIONS = [
  "all",
  "tokens",
  "primitives",
  "motion",
  "overlays",
  "shells",
] as const;

export type DesignSystemTheme = (typeof DESIGN_SYSTEM_THEMES)[number];
export type DesignSystemPreview = (typeof DESIGN_SYSTEM_PREVIEWS)[number];
export type DesignSystemLegacySection =
  (typeof DESIGN_SYSTEM_LEGACY_SECTIONS)[number];

export interface DesignSystemRouteSearch {
  preview?: DesignSystemPreview;
  /** Legacy search param kept only so old links can redirect to a page slug. */
  section?: DesignSystemLegacySection;
  theme: DesignSystemTheme;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isDesignSystemTheme(value: unknown): value is DesignSystemTheme {
  return (
    typeof value === "string" &&
    (DESIGN_SYSTEM_THEMES as readonly string[]).includes(value)
  );
}

function isDesignSystemPreview(value: unknown): value is DesignSystemPreview {
  return (
    typeof value === "string" &&
    (DESIGN_SYSTEM_PREVIEWS as readonly string[]).includes(value)
  );
}

function isDesignSystemLegacySection(
  value: unknown,
): value is DesignSystemLegacySection {
  return (
    typeof value === "string" &&
    (DESIGN_SYSTEM_LEGACY_SECTIONS as readonly string[]).includes(value)
  );
}

export function normalizeDesignSystemSearch(
  raw: unknown,
): DesignSystemRouteSearch {
  if (!isRecord(raw)) {
    return { theme: "light" };
  }

  const normalized: DesignSystemRouteSearch = {
    theme: isDesignSystemTheme(raw.theme) ? raw.theme : "light",
  };

  if (isDesignSystemLegacySection(raw.section)) {
    normalized.section = raw.section;
  }

  if (isDesignSystemPreview(raw.preview)) {
    normalized.preview = raw.preview;
  }

  return normalized;
}

export { DESIGN_SYSTEM_PREVIEWS, DESIGN_SYSTEM_THEMES };
